import {EventInput} from '@fullcalendar/core';
import plLocale from '@fullcalendar/core/locales/pl';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import {Paper} from '@mui/material';
import React, {useMemo} from 'react';
import {StatusCallModel} from '../../../model/status-calls/status-call.model';
import {statusCallColors} from '../utils/status-call.utils';

type Props = {
  calls: StatusCallModel[]
  onCallClick: (call: StatusCallModel) => void
  onSlotClick: (scheduledAtIso: string) => void
  onDateRangeChanged: (from: string, to: string) => void
};

const formatDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const StatusCallsCalendarView = ({calls, onCallClick, onSlotClick, onDateRangeChanged}: Props) => {
  const validRange = useMemo(() => ({start: formatDate(new Date())}), []);
  const events: EventInput[] = calls
    .filter(c => c.status !== 'CANCELLED')
    .map(call => {
      const start = new Date(call.scheduledAt);
      const end = new Date(start.getTime() + call.durationMinutes * 60_000);
      const color = statusCallColors[call.status];
      return {
        id: call.id,
        title: call.title ?? call.organizationName ?? 'Status call',
        start: start.toISOString(),
        end: end.toISOString(),
        backgroundColor: color,
        borderColor: color,
        textColor: 'white',
        extendedProps: {call},
      };
    });

  return (
    <Paper elevation={2} sx={{p: 1, borderRadius: '12px'}}>
      <FullCalendar
        plugins={[dayGridPlugin, interactionPlugin]}
        locale={plLocale}
        initialView="dayGridMonth"
        headerToolbar={{left: 'prev,next today', center: 'title', right: ''}}
        height="calc(100vh - 280px)"
        firstDay={1}
        events={events}
        selectable={true}
        validRange={validRange}
        selectAllow={(info) => info.start >= new Date()}
        eventClick={(info) => {
          const call: StatusCallModel = info.event.extendedProps?.call;
          if (call) onCallClick(call);
        }}
        dateClick={(info) => {
          if (info.date < new Date()) return;
          onSlotClick(info.dateStr);
        }}
        datesSet={(info) => {
          onDateRangeChanged(formatDate(info.start), formatDate(info.end));
        }}
      />
    </Paper>
  );
};
